import { create } from 'zustand';
import { addUser as addUserService, updateUserData, getUsersList, getCurrentUser, deleteUserAccount } from '../services/user.service';
import { User, AddUser, UpdateUserData } from '../types/user.type';

interface UserState {
  users: User[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  fetchUsers: (organization_id: string | null, limit?: number, offset?: number, search?: string) => Promise<void>;
  addUser: (data: AddUser) => Promise<User>;
  updateUserData: (user_id: string, data: UpdateUserData) => Promise<void>;
  deleteUser: (user_id: string) => Promise<void>;
  currentUser: User | null;
  fetchCurrentUser: () => Promise<void>;
}

export const useUserStore = create<UserState>((set, get) => ({
  users: [],
  isLoading: false,
  error: null,
  totalCount: 0,
  currentUser: null,

  fetchCurrentUser: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await getCurrentUser();
      set({ currentUser: response, isLoading: false });
    } catch (error: any) {
      set({ error: error.message || 'Error fetching current user', isLoading: false });
    }
  },
  fetchUsers: async (organization_id, limit, offset, search) => {
    const hasCached = get().users.length > 0 && !search;
    if (!hasCached) set({ isLoading: true, error: null });
    try {
      const response = await getUsersList(organization_id, limit, offset, search);
      set({ 
        users: response.result_list, 
        totalCount: response.total_count,
        isLoading: false 
      });
    } catch (error: any) {
      set({ error: error.message || 'Error fetching users', isLoading: false });
    }
  },

  addUser: async (data: AddUser) => {
    set({ isLoading: true, error: null });
    try {
      const createdUser = await addUserService(data);
      set({ isLoading: false });
      return createdUser;
    } catch (error: any) {
      set({ error: error.message || 'Error adding user', isLoading: false });
      throw error;
    }
  },

  updateUserData: async (user_id: string, data: UpdateUserData) => {
    set({ isLoading: true, error: null });
    try {
      await updateUserData(user_id, data);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message || 'Error updating user', isLoading: false });
      throw error;
    }
  },

  deleteUser: async (user_id: string) => {
    set({ isLoading: true, error: null });
    try {
      await deleteUserAccount(user_id);
      set((state) => ({
        users: state.users.filter((u) => u.id !== user_id),
        totalCount: state.totalCount - 1,
        isLoading: false,
      }));
    } catch (error: any) {
      set({ error: error.message || 'Error deleting user', isLoading: false });
      throw error;
    }
  },
})); 